import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

// Configuração global do axios para CORS
axios.defaults.withCredentials = false
axios.defaults.headers.common['Accept'] = 'application/json'

export interface TrelloBoard {
  id: string
  name: string
  desc: string
  closed: boolean
  url: string
  prefs: any
  memberships: any[]
}

export interface TrelloList {
  id: string
  name: string
  pos: number
  closed: boolean
}

export interface TrelloIntegration {
  id: number
  instance_id: number
  user_id: number
  trello_user_id: string
  trello_username: string
  is_active: boolean
  last_sync_at: string | null
  sync_frequency: 'daily' | 'weekly' | 'manual'
  webhook_id: string | null
  webhook_url: string | null
  settings: any
  created_at: string
  updated_at: string
}

export interface TrelloIntegrationStatus {
  is_connected: boolean
  integration: TrelloIntegration | null
}

class TrelloApiService {
  private getAuthHeaders() {
    // Usa a mesma chave de autenticação do sistema
    const authKey = 'impersonating-auth-token';
    const authData = localStorage.getItem(authKey);
    
    let token = null;
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        token = auth.api_token;
      } catch (error) {
        console.error('Erro ao parsear dados de autenticação:', error);
      }
    }
    
    // Fallback para outras chaves
    if (!token) {
      token = localStorage.getItem('access_token') || 
              localStorage.getItem('token') || 
              localStorage.getItem('auth_token');
    }
    
    console.log('Token para Trello API:', token ? 'Encontrado' : 'Não encontrado');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn('Nenhum token de autenticação encontrado para Trello API');
    }
    
    return headers;
  }

  /**
   * Obtém a URL de autorização OAuth do Trello
   */
  async getAuthorizationUrl(returnUrl: string): Promise<string> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trello/authorization-url`, {
        headers: this.getAuthHeaders(),
        params: { return_url: returnUrl }
      })

      if (response.data.success) {
        return response.data.data.authorization_url
      }

      throw new Error(response.data.message || 'Erro ao obter URL de autorização')
    } catch (error) {
      console.error('Error getting authorization URL:', error)
      throw error
    }
  }

  /**
   * Conecta uma integração Trello
   */
  async connect(accessToken: string, trelloUserId: string, trelloUsername: string): Promise<TrelloIntegration> {
    try {
      const payload: any = {
        access_token: accessToken
      };
      
      // Só envia os campos se não estiverem vazios
      if (trelloUserId && trelloUserId.trim()) {
        payload.trello_user_id = trelloUserId;
      }
      if (trelloUsername && trelloUsername.trim()) {
        payload.trello_username = trelloUsername;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/trello/connect`, payload, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.integration
      }

      throw new Error(response.data.message || 'Erro ao conectar integração')
    } catch (error) {
      console.error('Error connecting Trello:', error)
      throw error
    }
  }

  /**
   * Obtém o status da integração Trello
   */
  async getStatus(): Promise<TrelloIntegrationStatus> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trello/status`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter status da integração')
    } catch (error) {
      console.error('Error getting Trello integration status:', error)
      throw error
    }
  }

  /**
   * Obtém boards do usuário Trello
   */
  async getBoards(): Promise<TrelloBoard[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trello/boards`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.boards
      }

      throw new Error(response.data.message || 'Erro ao obter boards')
    } catch (error) {
      console.error('Error getting boards:', error)
      throw error
    }
  }

  /**
   * Obtém listas de um board específico
   */
  async getBoardLists(boardId: string): Promise<TrelloList[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trello/boards/${boardId}/lists`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.lists
      }

      throw new Error(response.data.message || 'Erro ao obter listas')
    } catch (error) {
      console.error('Error getting board lists:', error)
      throw error
    }
  }

  /**
   * Desconecta a integração Trello
   */
  async disconnect(): Promise<void> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/trello/disconnect`, {}, {
        headers: this.getAuthHeaders()
      })

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao desconectar')
      }
    } catch (error) {
      console.error('Error disconnecting Trello:', error)
      throw error
    }
  }

  /**
   * Atualiza configurações da integração
   */
  async updateSettings(settings: any): Promise<TrelloIntegration> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/trello/settings`, {
        settings
      }, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data.integration
      }

      throw new Error(response.data.message || 'Erro ao atualizar configurações')
    } catch (error) {
      console.error('Error updating settings:', error)
      throw error
    }
  }

  /**
   * Salva as chaves da API do Trello
   */
  async saveApiKeys(apiKey: string, apiSecret: string): Promise<void> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/trello/api-keys`, {
        api_key: apiKey,
        'api_secret': apiSecret
      }, {
        headers: this.getAuthHeaders()
      })

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao salvar chaves da API')
      }
    } catch (error) {
      console.error('Error saving API keys:', error)
      throw error
    }
  }

  /**
   * Verifica se as chaves da API estão configuradas
   */
  async hasApiKeys(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trello/api-keys`, {
        headers: this.getAuthHeaders()
      })

      return response.data.success && response.data.data?.has_keys === true
    } catch (error) {
      console.error('Error checking API keys:', error)
      return false
    }
  }
}

export const trelloApiService = new TrelloApiService()
